/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import {
  WarningOctagon, ArrowsClockwise, Trash, CaretRight, CaretDown, Sparkle,
  PencilSimple, Plus, X,
} from "@phosphor-icons/react";

export default function Failures() {
  const [rows, setRows] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [forceMode, setForceMode] = useState({}); // id → bool
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/extractions/failures"),
      api.get("/config/retry-policy"),
    ]).then(([r1, r2]) => { setRows(r1.data); setPolicy(r2.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/extractions/${id}/retry`);
      if (r.data.extraction_status === "succeeded") {
        toast.success("Retry succeeded — record created");
      } else if (r.data.extraction_status === "low_confidence") {
        toast.warning("Retry produced low-confidence output — review needed");
      } else {
        toast.error("Retry failed again — manual review needed");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setBusyId(null);
    }
  };

  const discard = async (id) => {
    if (!window.confirm("Discard this failed extraction? It will no longer appear in this list.")) return;
    setBusyId(id);
    try {
      await api.post(`/extractions/${id}/discard`);
      toast.success("Discarded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: "expand", header: "", sortable: false, width: 28,
      render: (r) => expanded[r.id] ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />,
    },
    {
      key: "created_at", header: "Received",
      render: (r) => <span className="text-mono" style={{ fontSize: 11 }}>
        {fmtDateTime(r.created_at)}
      </span>,
    },
    {
      key: "source", header: "Source",
      render: (r) => <span className="jde-badge confirmed">{r.source}</span>,
    },
    {
      key: "classified_as", header: "Type",
      render: (r) => <span className={`jde-badge ${r.classified_as === "po" ? "confirmed" : r.classified_as === "invoice" ? "partial" : "closed"}`}>{r.classified_as}</span>,
    },
    { key: "sender", header: "From",
      render: (r) => <span className="text-mono" style={{ fontSize: 11 }}>{r.sender || "—"}</span> },
    { key: "subject", header: "Subject",
      render: (r) => (
        <span style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
          {r.subject || "(no subject)"}
        </span>
      ) },
    {
      key: "extraction_status", header: "Status",
      render: (r) => <span className={`jde-badge ${r.extraction_status === "failed" ? "alert" : "pending"}`}>
        {r.extraction_status === "failed" ? "Failed" : "Low Confidence"}
      </span>,
    },
    {
      key: "retry_count", header: "Attempts", align: "right",
      render: (r) => `${r.retry_count || 0} / ${policy?.max_total_attempts ?? 9}`,
    },
    {
      key: "actions", header: "Actions", sortable: false,
      render: (r) => (
        <div style={{ display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="jde-btn jde-btn-sm"
            disabled={busyId === r.id}
            onClick={() => retry(r.id)}
            data-testid={`retry-${r.id}`}>
            <ArrowsClockwise size={11} weight="bold" /> Retry
          </button>
          <button className="jde-btn jde-btn-sm jde-btn-primary"
            disabled={busyId === r.id}
            onClick={() => {
              setExpanded((s) => ({ ...s, [r.id]: true }));
              setForceMode((s) => ({ ...s, [r.id]: true }));
            }}
            data-testid={`force-${r.id}`}>
            <PencilSimple size={11} weight="bold" /> Force Entry
          </button>
          <button className="jde-btn jde-btn-sm jde-btn-danger"
            disabled={busyId === r.id}
            onClick={() => discard(r.id)}
            data-testid={`discard-${r.id}`}>
            <Trash size={11} weight="bold" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout breadcrumb={["Ingestion Failures"]}>
      <div className="jde-toolbar">
        <WarningOctagon size={14} weight="bold" style={{ color: "var(--omh-alert)" }} />
        <span style={{ fontWeight: 700, fontSize: 13 }}>INGESTION FAILURES & LOW-CONFIDENCE EXTRACTIONS</span>
        <div className="divider" />
        <span className="muted" style={{ fontSize: 12 }}>
          {policy?.background_enabled
            ? `Auto-retry every ${policy.background_interval_minutes} min · cap ${policy.max_total_attempts} total attempts`
            : "Background auto-retry: disabled"}
        </span>
        <div style={{ flex: 1 }} />
        <button className="jde-btn" onClick={load} disabled={loading} data-testid="failures-refresh">
          <ArrowsClockwise size={12} weight="bold" /> Refresh
        </button>
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }}>
        {!loading && rows.length === 0 ? (
          <div className="jde-panel-body" style={{ textAlign: "center", padding: 36 }} data-testid="failures-empty">
            <div className="success-text" style={{ fontSize: 15 }}>
              <Sparkle size={16} weight="fill" style={{ display: "inline", marginRight: 6, marginBottom: -2 }} />
              All clear — no extraction failures
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Every recent ingestion produced a usable PO or invoice.
            </div>
          </div>
        ) : (
          <SortableGrid pageSize={100}
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.id}
            onRowClick={(r) => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
            defaultSort={{ key: "created_at", dir: "desc" }}
            searchPlaceholder="Search subject, sender, status, source…"
            testid="failures-grid"
            statusBar={(visible, all) => (
              <>
                <span>Failures: {visible.length}{visible.length !== all.length ? ` of ${all.length}` : ""}</span>
                <span>
                  {policy?.background_enabled
                    ? `Auto-retry: every ${policy.background_interval_minutes} min · stops after ${policy.max_total_attempts} attempts`
                    : `Auto-retry: off · ${policy?.internal_attempts_per_cycle ?? 3} attempts per manual cycle`}
                </span>
              </>
            )}
          />
        )}
      </div>

      {/* Expanded details panels */}
      {rows.filter((r) => expanded[r.id]).map((r) => (
        <div key={r.id} className="jde-panel" style={{ marginTop: 12 }} data-testid={`failure-detail-${r.id}`}>
          <div className="jde-panel-header">
            <span>{forceMode[r.id] ? "Force Manual Entry · " : "Details · "}{r.subject || "(no subject)"}</span>
            <div style={{ display: "flex", gap: 6 }}>
              {!forceMode[r.id] && (
                <button className="jde-btn jde-btn-sm jde-btn-primary"
                  onClick={() => setForceMode((s) => ({ ...s, [r.id]: true }))}
                  data-testid={`force-open-${r.id}`}>
                  <PencilSimple size={11} weight="bold" /> Force Entry
                </button>
              )}
              <button className="jde-btn jde-btn-sm" onClick={() => {
                setExpanded((s) => ({ ...s, [r.id]: false }));
                setForceMode((s) => ({ ...s, [r.id]: false }));
              }}>Close</button>
            </div>
          </div>

          {forceMode[r.id]
            ? <ForceForm row={r} onSaved={() => { setForceMode((s) => ({ ...s, [r.id]: false })); load(); }} />
            : (
              <div className="jde-panel-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label className="jde-label">Last Error</label>
                  <div style={{
                    background: "#FEE2E2", border: "1px solid #FCA5A5",
                    padding: 10, borderRadius: 6, fontSize: 12, color: "#7F1D1D",
                  }}>
                    {r.last_error || "No specific error — output failed quality threshold."}
                  </div>

                  <label className="jde-label" style={{ marginTop: 12 }}>Attempt Log</label>
                  <div style={{
                    background: "var(--omh-bg-subtle)", border: "1px solid var(--omh-border)",
                    borderRadius: 6, padding: 8, fontSize: 11.5, fontFamily: "Inter, sans-serif",
                    maxHeight: 220, overflow: "auto",
                  }}>
                    {(r.attempt_log || []).map((a, i) => (
                      <div key={i} style={{ marginBottom: 4 }}>
                        #{a.attempt} · {(a.at || "").slice(11, 19)} ·
                        {a.confidence != null && <> conf={a.confidence}</>}
                        {a.has_key === false && <> · <span className="alert-text">missing key fields</span></>}
                        {a.has_key === true && <> · <span className="success-text">ok</span></>}
                        {a.error && <> · <span className="alert-text">{a.error}</span></>}
                      </div>
                    ))}
                    {(!r.attempt_log || r.attempt_log.length === 0) && <span className="muted">No attempt log recorded.</span>}
                  </div>
                </div>
                <div>
                  <label className="jde-label">Raw Source Text</label>
                  <pre style={{
                    background: "#F9FAFB", border: "1px solid var(--omh-border)",
                    padding: 10, borderRadius: 6, fontSize: 11, whiteSpace: "pre-wrap",
                    maxHeight: 320, overflow: "auto", margin: 0,
                  }} data-testid={`failure-raw-${r.id}`}>
                    {r.body_text || "(no body)"}
                    {(r.attachments || []).map((a) => `\n\n=== ${a.filename} ===\n${a.text || ""}`).join("")}
                  </pre>
                </div>
              </div>
            )}
        </div>
      ))}
    </Layout>
  );
}

function ForceForm({ row, onSaved }) {
  const draft = row.last_extracted_draft || {};
  const isInvoice = row.classified_as === "invoice";
  const [kind, setKind] = useState(isInvoice ? "invoice" : "po");
  const [busy, setBusy] = useState(false);

  // Common
  const [customerName, setCustomerName] = useState(draft.customer_name || "");
  const [customerEmail, setCustomerEmail] = useState(draft.customer_email || row.sender || "");
  const [currency, setCurrency] = useState(draft.currency || "USD");
  const [totalAmount, setTotalAmount] = useState(draft.total_amount ?? "");
  const [notes, setNotes] = useState(draft.notes || draft.extraction_notes || "");

  // PO-specific
  const [poNumber, setPoNumber] = useState(draft.po_number || "");
  const [orderDate, setOrderDate] = useState(draft.order_date || "");
  const [reqShipDate, setReqShipDate] = useState(draft.requested_ship_date || "");
  const [shipTo, setShipTo] = useState(draft.ship_to_address || "");
  const [billTo, setBillTo] = useState(draft.bill_to_address || "");

  // Invoice-specific
  const [invoiceNumber, setInvoiceNumber] = useState(draft.invoice_number || "");
  const [invoiceDate, setInvoiceDate] = useState(draft.invoice_date || "");
  const [shipDate, setShipDate] = useState(draft.ship_date || "");
  const [tracking, setTracking] = useState(draft.tracking_number || "");
  const [carrier, setCarrier] = useState(draft.carrier || "");

  // Line items
  const initialLines = Array.isArray(draft.line_items) && draft.line_items.length > 0
    ? draft.line_items.map((li) => ({
        sku: li.sku || "",
        description: li.description || "",
        quantity_ordered: li.quantity_ordered ?? li.quantity_shipped ?? 0,
        quantity_shipped: li.quantity_shipped ?? 0,
        unit_price: li.unit_price ?? "",
        uom: li.uom || "",
      }))
    : [{ sku: "", description: "", quantity_ordered: 0, quantity_shipped: 0, unit_price: "", uom: "" }];
  const [lines, setLines] = useState(initialLines);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { sku: "", description: "", quantity_ordered: 0, quantity_shipped: 0, unit_price: "", uom: "" }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));

  const submit = async () => {
    if (kind === "po" && !poNumber.trim() && !customerName.trim()) {
      return toast.error("PO# or Customer name is required");
    }
    if (kind === "invoice" && !invoiceNumber.trim()) {
      return toast.error("Invoice # is required");
    }
    setBusy(true);
    try {
      const payload = {
        kind,
        customer_name: customerName || null,
        customer_email: customerEmail || null,
        currency: currency || "USD",
        total_amount: totalAmount === "" ? null : Number(totalAmount),
        notes: notes || null,
        line_items: lines
          .filter((l) => l.description || l.sku || Number(l.quantity_ordered) > 0)
          .map((l) => ({
            sku: l.sku || null,
            description: l.description || "",
            quantity_ordered: Number(l.quantity_ordered) || 0,
            quantity_shipped: Number(l.quantity_shipped) || 0,
            unit_price: l.unit_price === "" ? null : Number(l.unit_price),
            uom: l.uom || null,
          })),
        po_number: poNumber || null,
        order_date: orderDate || null,
        requested_ship_date: reqShipDate || null,
        ship_to_address: shipTo || null,
        bill_to_address: billTo || null,
        invoice_number: invoiceNumber || null,
        invoice_date: invoiceDate || null,
        ship_date: shipDate || null,
        tracking_number: tracking || null,
        carrier: carrier || null,
      };
      const r = await api.post(`/extractions/${row.id}/force`, payload);
      if (kind === "po") {
        toast.success(`PO ${r.data.po_number || ""} created — open it to verify`);
      } else {
        toast.success(r.data.matched_po_id
          ? `Invoice ${r.data.invoice_number} matched to PO ${r.data.po_number}`
          : `Invoice ${r.data.invoice_number} saved (no matching PO)`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force entry failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="jde-panel-body" data-testid={`force-form-${row.id}`}>
      <div style={{
        background: "#FEF3C7", border: "1px solid #FCD34D",
        padding: 10, fontSize: 12, borderRadius: 6, marginBottom: 14, color: "#78350F",
      }}>
        <strong>Manual override mode.</strong> Fields are pre-filled with the AI&apos;s best guess
        from {row.retry_count || 0} attempt(s). Edit, then save to create the record bypassing AI.
      </div>

      <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="radio" checked={kind === "po"} onChange={() => setKind("po")} data-testid="force-kind-po" />
          Purchase Order
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="radio" checked={kind === "invoice"} onChange={() => setKind("invoice")} data-testid="force-kind-invoice" />
          Invoice
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
        {kind === "po" ? (
          <>
            <Field label="PO Number" value={poNumber} onChange={setPoNumber} testid="force-po-number" />
            <Field label="Customer Name" value={customerName} onChange={setCustomerName} testid="force-customer" />
            <Field label="Customer Email" value={customerEmail} onChange={setCustomerEmail} testid="force-cust-email" />
            <Field label="Order Date" value={orderDate} onChange={setOrderDate} testid="force-order-date" />
            <Field label="Requested Ship Date" value={reqShipDate} onChange={setReqShipDate} testid="force-ship-date" />
            <Field label="Currency" value={currency} onChange={setCurrency} testid="force-currency" />
            <Field label="Total Amount" value={totalAmount} onChange={setTotalAmount} type="number" testid="force-total" />
          </>
        ) : (
          <>
            <Field label="Invoice Number" value={invoiceNumber} onChange={setInvoiceNumber} testid="force-inv-number" />
            <Field label="PO Number (to match)" value={poNumber} onChange={setPoNumber} testid="force-inv-po" />
            <Field label="Customer Name" value={customerName} onChange={setCustomerName} testid="force-inv-customer" />
            <Field label="Invoice Date" value={invoiceDate} onChange={setInvoiceDate} testid="force-inv-date" />
            <Field label="Ship Date" value={shipDate} onChange={setShipDate} testid="force-inv-ship-date" />
            <Field label="Carrier" value={carrier} onChange={setCarrier} testid="force-inv-carrier" />
            <Field label="Tracking #" value={tracking} onChange={setTracking} testid="force-inv-tracking" />
            <Field label="Total Amount" value={totalAmount} onChange={setTotalAmount} type="number" testid="force-inv-total" />
          </>
        )}
      </div>

      {kind === "po" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <div>
            <label className="jde-label">Ship-To Address</label>
            <textarea className="jde-textarea" rows={2} value={shipTo} onChange={(e) => setShipTo(e.target.value)} />
          </div>
          <div>
            <label className="jde-label">Bill-To Address</label>
            <textarea className="jde-textarea" rows={2} value={billTo} onChange={(e) => setBillTo(e.target.value)} />
          </div>
        </div>
      )}

      <label className="jde-label" style={{ marginTop: 14 }}>Line Items</label>
      <table className="jde-grid" data-testid="force-lines-grid">
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>SKU</th>
            <th>Description</th>
            {kind === "po" && <th>UOM</th>}
            <th className="num">{kind === "po" ? "Qty Ordered" : "Qty Shipped"}</th>
            <th className="num">Unit Price</th>
            <th style={{ width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="muted">{i + 1}</td>
              <td><input className="jde-input" value={l.sku} onChange={(e) => setLine(i, "sku", e.target.value)} /></td>
              <td><input className="jde-input" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
              {kind === "po" && (
                <td style={{ width: 70 }}><input className="jde-input" value={l.uom} onChange={(e) => setLine(i, "uom", e.target.value)} /></td>
              )}
              <td className="num" style={{ width: 100 }}>
                <input className="jde-input num" type="number"
                  value={kind === "po" ? l.quantity_ordered : l.quantity_shipped}
                  onChange={(e) => setLine(i, kind === "po" ? "quantity_ordered" : "quantity_shipped", e.target.value)} />
              </td>
              <td className="num" style={{ width: 100 }}>
                <input className="jde-input num" type="number" value={l.unit_price}
                  onChange={(e) => setLine(i, "unit_price", e.target.value)} />
              </td>
              <td>
                <button className="jde-btn jde-btn-sm jde-btn-danger" onClick={() => removeLine(i)}>
                  <X size={10} weight="bold" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 6 }}>
        <button className="jde-btn jde-btn-sm" onClick={addLine} data-testid="force-add-line">
          <Plus size={11} weight="bold" /> Add Line
        </button>
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="jde-btn jde-btn-primary" onClick={submit} disabled={busy} data-testid={`force-submit-${row.id}`}>
          {busy ? "Saving…" : `Create ${kind === "po" ? "PO" : "Invoice"} & Mark Resolved`}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="jde-label">{label}</label>
      <input
        className="jde-input"
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
